import React, { useEffect, useState } from "react";
import { Form, Container, Row, Col, Card } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  addDayVehicle,
  getoptions,
  editDayVehicle,
} from "../../store/slices/package";
import { getvehicleType } from "../../store/slices/vehicletype";
import { toast } from "react-toastify";
import Select from "react-select";

export default function CopyCarStep({
  packageId,
  cars,
  setCars,
  nextStep,
  prevStep,
}) {
  const dispatch = useDispatch();

  const [options, setOptions] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [errors, setErrors] = useState([]);

  // Fetch Options & Vehicles
  useEffect(() => {
    if (!packageId) return;

    const fetchData = async () => {
      try {
        const optRes = await dispatch(getoptions({ packageId })).unwrap();

        const vtRes = await dispatch(getvehicleType()).unwrap();

        const opt = optRes?.data || [];
        const vt = vtRes?.vehicles || [];

        setOptions(opt);
        setVehicleTypes(vt);
        setCars((prevCars) => {
          // First time
          if (prevCars.length === 0) {
            return opt.map((option) => ({
              _id: null,
              packageOption: option._id,
              vehicleType: "",
              pricePerDay: "",
              remarks: "",
            }));
          }

          // Keep existing cars
          const updatedCars = prevCars.map((car, idx) => ({
            ...car,
            packageOption: opt[idx]?._id || car.packageOption,
          }));

          // Add configs for newly added options
          opt.forEach((option) => {
            const exists = updatedCars.some(
              (car) =>
                String(car.packageOption) === String(option._id)
            );

            if (!exists) {
              updatedCars.push({
                _id: null,
                packageOption: option._id,
                vehicleType: "",
                pricePerDay: "",
                remarks: "",
              });
            }
          });

          return updatedCars;
        });
      } catch (err) {
        toast.error("Failed to load vehicle data");
      }
    };

    fetchData();
  }, [packageId, dispatch]);

  const handleChange = (index, field, value) => {
    const updated = [...cars];
    const newErrors = [...errors];

    if (!newErrors[index]) newErrors[index] = {};

    if (field === "vehicleType") {
      if (!value) {
        newErrors[index].vehicleType = "Please select vehicle type";
      } else {
        newErrors[index].vehicleType = "";
      }
    }

    if (field === "remarks") {
      if (value && !/^[a-zA-Z\s.,-]*$/.test(value)) {
        newErrors[index].remarks = "Only letters allowed";
        return; // block numbers
      } else {
        newErrors[index].remarks = "";
      }
    }

    if (field === "pricePerDay") {
      if (!value) {
        newErrors[index].pricePerDay = "Please enter price per day";
      } else if (!/^\d+$/.test(value)) {
        newErrors[index].pricePerDay = "Only numbers allowed";
        return;
      } else {
        newErrors[index].pricePerDay = "";
      }
    }

    updated[index][field] = value;
    setCars(updated);
    setErrors(newErrors);
  };

  const validateVehicleForm = () => {
    let isValid = true;
    const newErrors = [];

    cars.forEach((config, index) => {
      newErrors[index] = {};

      if (!config.vehicleType) {
        newErrors[index].vehicleType = "Please select vehicle type";
        isValid = false;
      }

      if (config.remarks && !/^[a-zA-Z\s.,-]*$/.test(config.remarks)) {
        newErrors[index].remarks = "Only letters allowed";
        isValid = false;
      }
      if (!config.pricePerDay) {
        newErrors[index].pricePerDay = "Please enter price per day";
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateVehicleForm()) return;

    try {
      for (const config of cars) {
        const payload = {
          packageId,
          packageOption: config.packageOption,
          vehicleType: config.vehicleType,
          remarks: config.remarks,
          pricePerDay: Number(config.pricePerDay),
        };

        if (
          config._id
        ) {
          // UPDATE
          await dispatch(
            editDayVehicle({
              id: config._id,
              ...payload,
            }),
          ).unwrap();
        } else {
          // CREATE
          const res = await dispatch(addDayVehicle(payload)).unwrap();
          config._id = res?._id || res?.data?._id || res?.vehicle?._id || null;
        }
      }

      toast.success("Vehicle configuration saved successfully");
      nextStep();
    } catch (err) {
      toast.error("Failed to save vehicles");
    }
  };

  return (
    <Container>
      <Card className="p-4 shadow-sm border-0 mt-2">
        {cars.map((config, index) => {
          const option = options.find(
            (o) =>
              String(o._id) ===
              String(
                config.packageOption
              )
          );

          return (
            <Card key={index} className="mb-4 p-3 border">
              <h6>Option: {option?.name}</h6>

              <Row className="g-3">
                {/* Vehicle Type */}
                <Col md={4}>
                  <Form.Label>Vehicle Type</Form.Label>
                  <Select
                    placeholder="Select Vehicle"
                    value={
                      vehicleTypes
                        .map((vt) => ({
                          value: vt._id,
                          label: vt.name,
                        }))
                        .find(
                          (o) =>
                            String(o.value) ===
                            String(
                              config.vehicleType
                            )
                        )
                    }
                    onChange={(selected) =>
                      handleChange(
                        index,
                        "vehicleType",
                        selected?.value || "",
                      )
                    }
                    options={vehicleTypes.map((vt) => ({
                      value: vt._id,
                      label: vt.name,
                    }))}
                  />
                  {errors[index]?.vehicleType && (
                    <small className="text-danger">
                      {errors[index].vehicleType}
                    </small>
                  )}
                </Col>

                {/* Price Per Day */}
                <Col md={4}>
                  <Form.Label>Price Per Day</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min="0"
                    value={config.pricePerDay}
                    placeholder="Enter price"
                    onChange={(e) =>
                      handleChange(index, "pricePerDay", e.target.value)
                    }
                  />
                  {errors[index]?.pricePerDay && (
                    <small className="text-danger">
                      {errors[index].pricePerDay}
                    </small>
                  )}
                </Col>

                {/* Remarks */}
                <Col md={4}>
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={config.remarks}
                    placeholder="Optional remarks"
                    onChange={(e) =>
                      handleChange(index, "remarks", e.target.value)
                    }
                  />
                </Col>
              </Row>
            </Card>
          );
        })}

        <div className="d-flex justify-content-between mt-4 mb-5">
          <button className="cancel-btn" onClick={prevStep}>
            Previous
          </button>

          <button className="submit-btn" onClick={handleSubmit}>
            Next
          </button>
        </div>
      </Card>
    </Container>
  );
}
